import { useState, useEffect, useCallback } from 'react'
import { useAdvancedFilters } from '../../hooks/useAdvancedFilters'
import type { FilterField, FilterValues } from '@/shared/components/entities'


export interface EntityListPageConfig<TEntity, TInput> {
  entityType: string // Ex: 'groups', 'categories'
  viewModeStorageKey?: string
  defaultViewMode?: 'card' | 'table'
  preferDefaultViewMode?: boolean
  filterFields: FilterField[]
  // Predicate to determine whether an entity can be selected in bulk operations
  // If omitted, all entities are selectable.
  isEntitySelectable?: (entity: TEntity) => boolean
  
  // Funções CRUD
  loadData: () => Promise<TEntity[]>
  createEntity: (data: TInput) => Promise<TEntity>
  updateEntity: (id: string, data: TInput) => Promise<TEntity>
  deleteEntity: (id: string) => Promise<void>
  
  // Função de filtro customizada
  applyFilters: (entities: TEntity[], searchQuery: string, filterValues: FilterValues) => TEntity[]
  
  // Validação de formulário
  validateForm?: (data: TInput) => Record<string, string>
  
  // Translation key prefix (opcional)
  translationPrefix?: string
}

export function useEntityListPage<TEntity, TInput>(config: EntityListPageConfig<TEntity, TInput>) {
  const {
    entityType,
    viewModeStorageKey,
    defaultViewMode = 'card',
    preferDefaultViewMode = false,
    filterFields,
    loadData,
    createEntity,
    updateEntity,
    deleteEntity,
    applyFilters,
    validateForm
  } = config

  // Data states
  const [entities, setEntities] = useState<TEntity[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // UI states
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    try {
      if (!preferDefaultViewMode) {
        const saved = localStorage.getItem(viewModeStorageKey ?? `${entityType}-viewMode`)
        if (saved === 'table' || saved === 'card') return saved
      }
    } catch { /* ignore */ }
    return defaultViewMode
  })
  
  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  
  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [editingEntity, setEditingEntity] = useState<TEntity | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<TEntity | null>(null)
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)

  // Form state
  const [formData, setFormData] = useState<TInput>({} as TInput)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Advanced filters
  const {
    filterValues,
    setFilterValues,
    savedFilters,
    saveFilter,
    loadFilter,
    deleteFilter
  } = useAdvancedFilters({
    entityType,
    userId: undefined, // Pode ser configurado depois
    onFiltersChange: (filters) => {
      console.log('Filters changed:', filters)
    }
  })

  // Filtered entities
  const filteredEntities = applyFilters(entities, searchQuery, filterValues)

  // Count active filters
  const activeFiltersCount = Object.keys(filterValues).filter(key => {
    const value = filterValues[key]
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.values(value).some(v => v !== null && v !== undefined && v !== '')
    }
    return true
  }).length

  // Load data
  useEffect(() => {
    handleLoadData()
  }, [])

  const handleLoadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await loadData()
      setEntities(data)
    } catch (err) {
      console.error('Error loading data:', err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  // Clear filters
  const handleClearFilters = () => {
    const clearedValues: FilterValues = {}
    filterFields.forEach(field => {
      if (field.type === 'daterange') {
        clearedValues[field.key] = { start: null, end: null }
      } else if (field.type === 'boolean') {
        clearedValues[field.key] = null
      } else {
        clearedValues[field.key] = ''
      }
    })
    setFilterValues(clearedValues)
  }

  // Modal handlers
  const handleOpenModal = (entity?: TEntity, mode?: 'create' | 'edit' | 'view') => {
    try {
      console.debug('[useEntityListPage] handleOpenModal called', { id: (entity as any)?.id, mode })
    } catch (err) { /* ignore */ }
    setEditingEntity(entity || null)
    // Infer mode when not provided
    if (mode) {
      setModalMode(mode)
    } else {
      setModalMode(entity ? 'edit' : 'create')
    }
    // Reset formData only when creating new (not when editing)
    if (!entity && !mode) {
      setFormData({} as TInput)
    }
    setShowModal(true)
    setFormErrors({})
    try { console.debug('[useEntityListPage] after setShowModal', { showModal: true, editingId: (entity as any)?.id }) } catch (e) {}
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingEntity(null)
    setModalMode('create')
    // Don't reset formData here - preserved for potential quick re-open
    // EntityListPage useEffect will initialize based on editingEntity when modal opens
    setFormErrors({})
  }

  // Form validation
  const handleValidateForm = useCallback((): boolean => {
    if (!validateForm) return true
    
    const errors = validateForm(formData)
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, validateForm])

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!handleValidateForm()) {
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      if (editingEntity && typeof editingEntity === 'object' && editingEntity !== null && 'id' in editingEntity) {
        const id = (editingEntity as any).id
        await updateEntity(id, formData)
        setSuccess('Registro atualizado com sucesso!')
      } else {
        await createEntity(formData)
        setSuccess('Registro criado com sucesso!')
      }

      await handleLoadData()
      handleCloseModal()
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error submitting:', err)
      setError(err instanceof Error ? err.message : 'Erro ao salvar registro')
    } finally {
      setSubmitting(false)
    }
  }

  // Delete handler
  const handleDelete = async () => {
    if (!confirmDelete || typeof confirmDelete !== 'object' || confirmDelete === null || !('id' in confirmDelete)) return

    try {
      setSubmitting(true)
      setError(null)
      const id = (confirmDelete as any).id
      await deleteEntity(id)
      setSuccess('Registro excluído com sucesso!')
      await handleLoadData()
      setConfirmDelete(null)
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error deleting:', err)
      setError(err instanceof Error ? err.message : 'Erro ao excluir registro')
    } finally {
      setSubmitting(false)
    }
  }

  // Bulk selection handlers
  const isSelectable = (entity: TEntity) => {
    try {
      return typeof config.isEntitySelectable === 'function' ? config.isEntitySelectable(entity) : true
    } catch (err) {
      return true
    }
  }

  const toggleSelection = useCallback((id: string) => {
    // Find the entity by id in the loaded sets (prefer displayed items)
    const found = entities.find((e: any) => e && typeof e === 'object' && 'id' in e && (e as any).id === id)
      || filteredEntities.find((e: any) => e && typeof e === 'object' && 'id' in e && (e as any).id === id)
    if (!found || !isSelectable(found)) return

    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [entities, filteredEntities])

  const toggleSelectAll = useCallback(() => {
    const selectableFiltered = filteredEntities
      .filter((entity): entity is TEntity & { id: string } =>
        typeof entity === 'object' && entity !== null && 'id' in entity && isSelectable(entity)
      )

    if (selectedIds.size === selectableFiltered.length && selectableFiltered.length > 0) {
      // Deselect all selectable
      setSelectedIds(new Set())
    } else {
      // Select only selectable items from current filtered results
      const allIds = selectableFiltered.map(entity => entity.id)
      setSelectedIds(new Set(allIds))
    }
  }, [filteredEntities, selectedIds.size])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const selectableFilteredCount = filteredEntities.filter(e => isSelectable(e)).length
  const isAllSelected = selectedIds.size === selectableFilteredCount && selectableFilteredCount > 0
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < selectableFilteredCount

  // Sanitize selection when data or selectability predicate changes: remove ids that are no longer selectable
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev
      const newSet = new Set<string>()
      for (const id of prev) {
        const ent = entities.find((e: any) => e && typeof e === 'object' && 'id' in e && (e as any).id === id)
          || filteredEntities.find((e: any) => e && typeof e === 'object' && 'id' in e && (e as any).id === id)
        if (ent && isSelectable(ent)) newSet.add(id)
      }
      // If unchanged, keep same object to avoid unnecessary re-renders
      if (newSet.size === prev.size && Array.from(newSet).every(x => prev.has(x))) return prev
      return newSet
    })
  }, [entities, filteredEntities, config.isEntitySelectable])

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return

    try {
      setSubmitting(true)
      setError(null)
      
      // Delete all selected items
      const deletePromises = Array.from(selectedIds).map(id => deleteEntity(id))
      await Promise.all(deletePromises)
      
      setSuccess(`${selectedIds.size} registro(s) excluído(s) com sucesso!`)
      clearSelection()
      await handleLoadData()
      setConfirmBulkDelete(false)
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error bulk deleting:', err)
      setError(err instanceof Error ? err.message : 'Erro ao excluir registros')
    } finally {
      setSubmitting(false)
    }
  }

  // Clear selection when filters change
  // NOTE: Previously selection was cleared when search or filters changed.
  // Keep selection stable across filtering as requested: users expect
  // checked items to remain selected until they explicitly clear selection
  // or items become non-selectable or are removed from the dataset.

  return {
    // Data
    entities,
    filteredEntities,
    loading,
    submitting,
    error,
    success,
    
    // UI States
    searchQuery,
    setSearchQuery,
    viewMode,
    setViewMode: (mode: 'card' | 'table') => {
      try { localStorage.setItem(viewModeStorageKey ?? `${entityType}-viewMode`, mode) } catch { /* ignore */ }
      setViewMode(mode)
    },
    setViewModeTemporary: setViewMode,
    
    // Bulk selection
    selectedIds,
    isAllSelected,
    isSomeSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    
    // Modal states
    showModal,
    modalMode,
    showAdvancedFilters,
    setShowAdvancedFilters,
    editingEntity,
    confirmDelete,
    setConfirmDelete,
    confirmBulkDelete,
    setConfirmBulkDelete,
    
    // Form
    formData,
    setFormData,
    formErrors,
    
    // Filters
    filterValues,
    setFilterValues,
    savedFilters,
    activeFiltersCount,
    
    // Handlers
    handleLoadData,
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    handleDelete,
    handleBulkDelete,
    handleClearFilters,
    saveFilter,
    loadFilter,
    deleteFilter
  }
}
